import React, { createContext } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { useSharedCaption } from '@utils/providers/shared-caption-context.tsx';
import { useNumpadShortcutsDisabled } from '@utils/hooks/use-numpad-shortcuts-disabled.ts';
import { useCaptionSegmentHotkeys } from '@utils/hooks/use-caption-segment-hotkeys.ts';

interface SharedCaptionHotkeyContextType {}

const SharedCaptionHotkeyContext = createContext<
  SharedCaptionHotkeyContextType | undefined
>(undefined);

interface SharedCaptionHotkeyProviderProps {
  children: React.ReactNode;
}

export function SharedCaptionHotkeyProvider({
  children
}: SharedCaptionHotkeyProviderProps) {
  const {
    saveDocument,
    searchInputRef,
    clickedIndex,
    addListItem,
    splitListItem,
    removeListItem,
    setClickedIndex,
    document: captionDocument,
    changeSegmentTimeForCurrentIndex
  } = useSharedCaption();
  const {
    play,
    pause,
    isPlaying,
    rewind,
    forward,
    setTime,
    toggleLinkedPlayback
  } = useAudio();

  // #4029 — Profile → Keyboard flag. Anonymous share-link visitors fall
  // through to the default (numpad enabled), so existing embed users are
  // untouched. Authenticated visitors get their own preference honoured
  // even inside a shared editor.
  const numpadDisabled = useNumpadShortcutsDisabled();

  // Ctrl/Cmd+Z and Ctrl/Cmd+Y are handled in shared-caption-main (outside a
  // segment) and in the segment editor itself.

  // space or numpad 5 play pause. `numpad5` also matches the top-row 5, so
  // check the physical key.
  useHotkeys(['space', 'numpad5'], event => {
    if (event.key !== ' ' && (numpadDisabled || event.code !== 'Numpad5')) {
      return;
    }
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  });

  // F5 Play/Pause - works while editing (contentEditable)
  useHotkeys(
    ['f5'],
    event => {
      event.preventDefault();
      if (isPlaying) {
        pause();
      } else {
        play();
      }
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // F7 Rewind 5 seconds - works while editing (contentEditable)
  useHotkeys(
    ['f7'],
    event => {
      event.preventDefault();
      rewind(5);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // F8 Forward 5 seconds - works while editing (contentEditable)
  useHotkeys(
    ['f8'],
    event => {
      event.preventDefault();
      forward(5);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // option or ctrl + arrow left rewind 5 seconds
  useHotkeys(
    ['left', 'ctrl+left', 'meta+left'], // 'meta+left' is for Mac
    () => {
      rewind(5);
    }
  );

  // option or ctrl + right arrow forward 5 seconds
  useHotkeys(
    ['right', 'ctrl+right', 'meta+right'], // 'meta+right' is for Mac
    () => {
      forward(5);
    }
  );

  // ctrl+s save document
  useHotkeys(['ctrl+s', 'meta+s'], event => {
    event.preventDefault();
    saveDocument(true);
  });

  // Playback-rate keyboard shortcuts have been removed. They previously bound
  // ctrl+plus / ctrl+= / ctrl+minus (and meta+…) and called preventDefault(),
  // which blocked the browser's native zoom (Utrecht #2XXX — Cladder/Stoffels
  // couldn't Ctrl+- to zoom out the page, it changed the playback speed
  // instead). Users can adjust playback rate via the dropdown in the media
  // controls.
  // ctrl+f focus search input
  useHotkeys(
    ['ctrl+f', 'meta+f'],
    event => {
      event.preventDefault();
      searchInputRef?.current?.focus();
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  // ctrl+l toggle linked playback
  useHotkeys(
    ['ctrl+l', 'meta+l'],
    event => {
      event.preventDefault();
      toggleLinkedPlayback();
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  useCaptionSegmentHotkeys({
    document: captionDocument,
    clickedIndex,
    setClickedIndex,
    addListItem,
    splitListItem,
    removeListItem,
    changeSegmentTimeForCurrentIndex,
    setTime,
    numpadDisabled
  });

  // Ctrl+K: extra shared-editor alias for "segment end -0.04s".
  useHotkeys(
    'ctrl+k',
    event => {
      event.preventDefault();
      changeSegmentTimeForCurrentIndex('stop', -40);
    },
    { enableOnFormTags: true, enableOnContentEditable: true }
  );

  return (
    <SharedCaptionHotkeyContext.Provider value={{}}>
      {children}
    </SharedCaptionHotkeyContext.Provider>
  );
}
